import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Trophy, Users, TrendingUp, Star, Award, School,
  Plus, RefreshCw, Calendar, Target, Zap, Settings, Crown,
  Search, ChevronDown, ChevronUp, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPETITION_LEVELS = [
  { key: 'cluster', name: 'Cluster / Inter School' },
  { key: 'zonal', name: 'Zonal Championship' },
  { key: 'sub_county', name: 'Sub County Championship' },
  { key: 'county', name: 'County Championship' },
  { key: 'regional', name: 'Regional Championship' },
  { key: 'national', name: 'National Championship' },
  { key: 'east_african', name: 'East African Championship' },
  { key: 'all_africa', name: 'All Africa Championship' },
];

const PERFORMANCE_STAGES = [
  { key: 'participation', name: 'Participation' },
  { key: 'round_of_32', name: 'Round of 32' },
  { key: 'round_of_16', name: 'Round of 16' },
  { key: 'quarterfinal', name: 'Quarterfinal' },
  { key: 'semifinal', name: 'Semifinal' },
  { key: 'finalist', name: 'Finalist' },
  { key: 'champion', name: 'Champion' },
];

const PLAYER_AWARDS = [
  { key: 'highest_scorer', name: 'Highest Scorer' },
  { key: 'best_defensive', name: 'Best Defensive Player' },
  { key: 'best_attacking', name: 'Best Attacking Player' },
  { key: 'best_midfielder', name: 'Best Midfielder' },
  { key: 'best_goalkeeper', name: 'Best Goalkeeper' },
  { key: 'most_promising', name: 'Most Promising Player' },
];

export default function RankingsTab({ token, athletes = [], tournaments = [] }) {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPointsRef, setShowPointsRef] = useState(false);

  // Filters
  const [resultSportFilter, setResultSportFilter] = useState('all');
  const [resultAthleteFilter, setResultAthleteFilter] = useState('all');
  const [resultAgeGroupFilter, setResultAgeGroupFilter] = useState('all');
  const [resultSearch, setResultSearch] = useState('');

  // Dialogs
  const [showSeasonDialog, setShowSeasonDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [seasonForm, setSeasonForm] = useState({ name: '', start_date: '', end_date: '', description: '' });
  const [resultForm, setResultForm] = useState({
    athlete_id: '', sport: '', age_group: '', competition_level: 'county',
    performance_stage: 'participation', player_awards: [], tournament_id: '', notes: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, seasonsRes, statsRes, resultsRes, rankingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/rankings/config`),
        axios.get(`${API_URL}/api/rankings/seasons`),
        axios.get(`${API_URL}/api/rankings/stats/overview`),
        axios.get(`${API_URL}/api/rankings/results`, { params: { limit: 200 } }),
        axios.get(`${API_URL}/api/rankings/athletes`, { params: { limit: 500 } }),
      ]);
      setConfig(configRes.data);
      setSeasons(seasonsRes.data.seasons || []);
      setStats(statsRes.data);
      setRecentResults(resultsRes.data.results || []);
      setRankings(rankingsRes.data.rankings || []);
      const active = (seasonsRes.data.seasons || []).find(s => s.is_active);
      setActiveSeason(active || null);
    } catch (err) {
      console.error('Failed to load rankings data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createSeason = async () => {
    try {
      await axios.post(`${API_URL}/api/rankings/seasons`, { ...seasonForm, is_active: true }, { headers });
      toast.success('Season created and activated');
      setShowSeasonDialog(false);
      setSeasonForm({ name: '', start_date: '', end_date: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create season');
    }
  };

  const submitResult = async () => {
    if (!resultForm.athlete_id || !resultForm.sport || !resultForm.competition_level || !resultForm.performance_stage) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const payload = { ...resultForm };
      if (!payload.age_group) delete payload.age_group;
      if (!payload.tournament_id) delete payload.tournament_id;
      if (!payload.notes) delete payload.notes;
      const res = await axios.post(`${API_URL}/api/rankings/results`, payload, { headers });
      toast.success(`Result recorded: ${res.data.points_breakdown.grand_total} points`);
      setShowResultDialog(false);
      setResultForm({
        athlete_id: '', sport: '', age_group: '', competition_level: 'county',
        performance_stage: 'participation', player_awards: [], tournament_id: '', notes: ''
      });
      setTimeout(fetchData, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit result');
    }
  };

  const triggerRecalculation = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/rankings/recalculate`, {}, { headers });
      toast.success(res.data.message);
      setTimeout(fetchData, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recalculation failed');
    }
  };

  const toggleAward = (key) => {
    setResultForm(prev => ({
      ...prev,
      player_awards: prev.player_awards.includes(key)
        ? prev.player_awards.filter(a => a !== key)
        : [...prev.player_awards, key]
    }));
  };

  // Build rank lookup: athlete_id+sport -> rank_position
  const rankLookup = {};
  rankings.forEach(r => {
    const key = `${r.athlete_id}__${r.sport}`;
    rankLookup[key] = r.rank_position;
  });

  // Get unique values for filter dropdowns
  const resultSports = [...new Set(recentResults.map(r => r.sport).filter(Boolean))].sort();
  const resultAthletes = [...new Set(recentResults.map(r => r.athlete_name).filter(Boolean))].sort();
  const resultAgeGroups = [...new Set(recentResults.map(r => r.age_group).filter(Boolean))].sort();

  // Apply all filters
  const filteredResults = recentResults.filter(r => {
    const matchSport = resultSportFilter === 'all' || r.sport === resultSportFilter;
    const matchAthlete = resultAthleteFilter === 'all' || r.athlete_name === resultAthleteFilter;
    const matchAgeGroup = resultAgeGroupFilter === 'all' || r.age_group === resultAgeGroupFilter;
    const matchSearch = !resultSearch ||
      (r.athlete_name || '').toLowerCase().includes(resultSearch.toLowerCase()) ||
      (r.sport || '').toLowerCase().includes(resultSearch.toLowerCase());
    return matchSport && matchAthlete && matchAgeGroup && matchSearch;
  });

  if (loading) return <div className="text-center py-8 text-gray-400">Loading rankings engine...</div>;

  return (
    <div className="space-y-6" data-testid="rankings-tab">
      {/* Header with actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" /> Rankings & Ratings Engine
          </h2>
          {activeSeason ? (
            <p className="text-sm text-white/40 mt-0.5">Active Season: <span className="text-emerald-400">{activeSeason.name}</span></p>
          ) : (
            <p className="text-sm text-orange-400 mt-0.5">No active season. Create one to start recording results.</p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowPointsRef(!showPointsRef)} className="border-white/10 text-white/70" data-testid="btn-points-ref">
            <BookOpen className="w-4 h-4 mr-1" /> Points Reference
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowSeasonDialog(true)} className="border-white/10 text-white/70" data-testid="btn-create-season">
            <Calendar className="w-4 h-4 mr-1" /> Manage Seasons
          </Button>
          <Button size="sm" variant="outline" onClick={triggerRecalculation} className="border-white/10 text-white/70" data-testid="btn-recalculate">
            <RefreshCw className="w-4 h-4 mr-1" /> Recalculate
          </Button>
          <Button size="sm" onClick={() => setShowResultDialog(true)} disabled={!activeSeason} className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" data-testid="btn-submit-result">
            <Plus className="w-4 h-4 mr-1" /> Submit Result
          </Button>
        </div>
      </div>

      {/* Points & Ranking System Reference */}
      {showPointsRef && config && <PointsReferencePanel config={config} />}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="ranking-stats-cards">
          {[
            { label: 'Ranked Athletes', value: stats.ranked_athletes, icon: Users, color: 'text-blue-400' },
            { label: 'ELO Rated', value: stats.rated_athletes, icon: TrendingUp, color: 'text-purple-400' },
            { label: 'Results', value: stats.competition_results, icon: Trophy, color: 'text-yellow-400' },
            { label: 'Talent Pool', value: stats.talent_pool_count, icon: Star, color: 'text-emerald-400' },
            { label: 'Schools', value: stats.schools_ranked, icon: School, color: 'text-cyan-400' },
            { label: 'Sponsorship Eligible', value: stats.sponsorship_eligible, icon: Award, color: 'text-orange-400' },
            { label: 'Seasons', value: stats.seasons_count, icon: Calendar, color: 'text-pink-400' },
          ].map((s, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/5 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                <span className="text-[10px] text-white/40 uppercase tracking-wider">{s.label}</span>
              </div>
              <p className="text-xl font-bold text-white">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Sport Distribution */}
      {stats?.sport_distribution?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
          <h3 className="text-sm font-medium text-white/60 mb-3">Rankings by Sport</h3>
          <div className="flex flex-wrap gap-2">
            {stats.sport_distribution.map(sd => (
              <span key={sd._id} className="text-xs bg-white/5 text-white/60 px-3 py-1.5 rounded border border-white/5">
                {sd._id}: <span className="text-white font-medium">{sd.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent Results with Filters */}
      <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-white/60">Recent Competition Results</h3>
            <span className="text-xs text-white/30">{filteredResults.length} of {recentResults.length} records</span>
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[150px] max-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <Input
                placeholder="Search..."
                value={resultSearch}
                onChange={e => setResultSearch(e.target.value)}
                className="pl-9 h-8 text-xs bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="result-search-input"
              />
            </div>
            <Select value={resultAthleteFilter} onValueChange={setResultAthleteFilter}>
              <SelectTrigger className="w-[170px] h-8 text-xs bg-white/5 border-white/10 text-white" data-testid="result-athlete-filter">
                <SelectValue placeholder="All Athletes" />
              </SelectTrigger>
              <SelectContent className="max-h-[250px]">
                <SelectItem value="all">All Athletes</SelectItem>
                {resultAthletes.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={resultSportFilter} onValueChange={setResultSportFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs bg-white/5 border-white/10 text-white" data-testid="result-sport-filter">
                <SelectValue placeholder="All Sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {resultSports.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={resultAgeGroupFilter} onValueChange={setResultAgeGroupFilter}>
              <SelectTrigger className="w-[130px] h-8 text-xs bg-white/5 border-white/10 text-white" data-testid="result-age-group-filter">
                <SelectValue placeholder="All Age Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Age Groups</SelectItem>
                {resultAgeGroups.map(ag => (
                  <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(resultSportFilter !== 'all' || resultAthleteFilter !== 'all' || resultAgeGroupFilter !== 'all' || resultSearch) && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-white/40 hover:text-white" onClick={() => { setResultSportFilter('all'); setResultAthleteFilter('all'); setResultAgeGroupFilter('all'); setResultSearch(''); }} data-testid="btn-clear-filters">
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {filteredResults.length === 0 ? (
          <div className="text-center py-8 text-white/30 text-sm">No results found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-results-table">
              <thead>
                <tr className="border-b border-white/5 text-white/30 text-xs uppercase tracking-wider">
                  <th className="px-4 py-2.5 text-left">Athlete</th>
                  <th className="px-3 py-2.5 text-left">Sport</th>
                  <th className="px-3 py-2.5 text-left">Age Group</th>
                  <th className="px-3 py-2.5 text-left">Level</th>
                  <th className="px-3 py-2.5 text-left">Stage</th>
                  <th className="px-3 py-2.5 text-right">Points</th>
                  <th className="px-3 py-2.5 text-center">National Rank</th>
                  <th className="px-3 py-2.5 text-left">Awards</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map(r => {
                  const natRank = rankLookup[`${r.athlete_id}__${r.sport}`];
                  return (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 text-white text-sm font-medium">{r.athlete_name}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs bg-white/5 text-white/60 px-2 py-0.5 rounded">{r.sport}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {r.age_group ? (
                          <span className="text-xs bg-cyan-500/15 text-cyan-400 px-2 py-0.5 rounded font-medium">{r.age_group}</span>
                        ) : (
                          <span className="text-white/20 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-white/50 text-sm capitalize">{(r.competition_level || '').replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2.5 text-white/50 text-sm capitalize">{(r.performance_stage || '').replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2.5 text-right text-yellow-400 font-bold text-sm">{r.grand_total}</td>
                      <td className="px-3 py-2.5 text-center" data-testid={`national-rank-${r.id}`}>
                        {natRank ? (
                          <span className={`text-xs font-bold px-2.5 py-1 rounded inline-block min-w-[36px] ${
                            natRank <= 3 ? 'bg-yellow-500/20 text-yellow-400' : natRank <= 10 ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-white/60'
                          }`}>
                            #{natRank}
                          </span>
                        ) : (
                          <span className="text-white/20 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(r.player_awards || []).map(a => (
                            <span key={a} className="text-[10px] bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded whitespace-nowrap">{a.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Season Dialog */}
      <Dialog open={showSeasonDialog} onOpenChange={setShowSeasonDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-pink-400" /> Manage Seasons</DialogTitle>
          </DialogHeader>
          {seasons.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-white/60 text-xs">Existing Seasons</Label>
              {seasons.map(s => (
                <div key={s.id} className={`flex items-center justify-between p-2 rounded border ${s.is_active ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
                  <div>
                    <span className="text-white text-sm">{s.name}</span>
                    {s.is_active && <span className="ml-2 text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">ACTIVE</span>}
                  </div>
                  <span className="text-white/30 text-xs">{s.start_date} — {s.end_date}</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3">
            <Label className="text-white/60 text-xs">Create New Season</Label>
            <Input placeholder="Season Name (e.g. 2026 Term 1)" value={seasonForm.name} onChange={e => setSeasonForm(p => ({ ...p, name: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="season-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/40 text-xs">Start Date</Label>
                <Input type="date" value={seasonForm.start_date} onChange={e => setSeasonForm(p => ({ ...p, start_date: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="season-start-input" />
              </div>
              <div>
                <Label className="text-white/40 text-xs">End Date</Label>
                <Input type="date" value={seasonForm.end_date} onChange={e => setSeasonForm(p => ({ ...p, end_date: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="season-end-input" />
              </div>
            </div>
            <Textarea placeholder="Description (optional)" value={seasonForm.description} onChange={e => setSeasonForm(p => ({ ...p, description: e.target.value }))} className="bg-white/5 border-white/10 text-white" rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSeasonDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={createSeason} disabled={!seasonForm.name || !seasonForm.start_date || !seasonForm.end_date} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-season">Create & Activate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-400" /> Submit Competition Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/60 text-xs">Athlete *</Label>
              <Select value={resultForm.athlete_id} onValueChange={v => setResultForm(p => ({ ...p, athlete_id: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="result-athlete-select">
                  <SelectValue placeholder="Select athlete" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {athletes.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.full_name || a.name} — {(a.sports || []).join(', ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Sport *</Label>
                <Select value={resultForm.sport} onValueChange={v => setResultForm(p => ({ ...p, sport: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="result-sport-select">
                    <SelectValue placeholder="Select sport" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.supported_sports || []).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Age Group</Label>
                <Select value={resultForm.age_group || 'auto'} onValueChange={v => setResultForm(p => ({ ...p, age_group: v === 'auto' ? '' : v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Auto from DOB" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto from DOB</SelectItem>
                    {(config?.age_groups || []).map(ag => (
                      <SelectItem key={ag.age_group_name} value={ag.age_group_name}>{ag.age_group_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Competition Level *</Label>
                <Select value={resultForm.competition_level} onValueChange={v => setResultForm(p => ({ ...p, competition_level: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="result-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPETITION_LEVELS.map(l => (
                      <SelectItem key={l.key} value={l.key}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Performance Stage *</Label>
                <Select value={resultForm.performance_stage} onValueChange={v => setResultForm(p => ({ ...p, performance_stage: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="result-stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERFORMANCE_STAGES.map(s => (
                      <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Tournament (optional)</Label>
              <Select value={resultForm.tournament_id || 'none'} onValueChange={v => setResultForm(p => ({ ...p, tournament_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="No tournament" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific tournament</SelectItem>
                  {tournaments.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.sport})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Player Awards (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PLAYER_AWARDS.map(a => (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => toggleAward(a.key)}
                    className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                      resultForm.player_awards.includes(a.key)
                        ? 'bg-purple-500/20 text-purple-400 border-purple-500/40'
                        : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`award-${a.key}`}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
            </div>
            <Textarea value={resultForm.notes} onChange={e => setResultForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" className="bg-white/5 border-white/10 text-white" rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={submitResult} className="bg-yellow-600 hover:bg-yellow-700" data-testid="btn-save-result">
              <Trophy className="w-4 h-4 mr-1" /> Submit Result
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== Points Reference Panel ===== */
function PointsReferencePanel({ config }) {
  const levels = config?.competition_levels || {};
  const stages = config?.performance_stages || {};
  const awards = config?.player_awards || {};
  const tiers = config?.sponsorship_tiers || {};
  const kFactors = config?.k_factors || {};

  return (
    <div className="bg-gradient-to-r from-[#0d1b2e] to-[#1a1a2e] border border-yellow-500/20 rounded-lg p-5 space-y-5" data-testid="points-reference-panel">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-yellow-400" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">Points & Ranking System Reference</h3>
      </div>
      <p className="text-white/50 text-xs">
        <strong className="text-white/70">Formula:</strong> Total Points = Base Points x Competition Multiplier + Award Points x Competition Multiplier
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <h4 className="text-xs text-yellow-400 font-medium uppercase tracking-wider mb-2">Competition Levels (Multiplier)</h4>
          <div className="space-y-1">
            {Object.entries(levels).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/70">{val.name || key}</span>
                <span className="text-yellow-400 font-bold">x{val.multiplier || val}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-blue-400 font-medium uppercase tracking-wider mb-2">Performance Stages (Base Points)</h4>
          <div className="space-y-1">
            {Object.entries(stages).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/70">{val.name || key}</span>
                <span className="text-blue-400 font-bold">{val.points || val} pts</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-purple-400 font-medium uppercase tracking-wider mb-2">Player Awards (Bonus Points)</h4>
          <div className="space-y-1">
            {Object.entries(awards).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/70">{typeof val === 'object' ? val.name : key.replace(/_/g, ' ')}</span>
                <span className="text-purple-400 font-bold">+{typeof val === 'object' ? val.points : val} pts</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-orange-400 font-medium uppercase tracking-wider mb-2">Sponsorship Tiers (Percentile)</h4>
          <div className="space-y-1">
            {Object.entries(tiers).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/70">{val.name || key}</span>
                <span className="text-orange-400 font-bold">Top {val.min_percentile}–{val.max_percentile}%</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs text-emerald-400 font-medium uppercase tracking-wider mb-2">ELO K-Factors (by Level)</h4>
          <div className="space-y-1">
            {Object.entries(kFactors).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs bg-white/[0.03] px-3 py-1.5 rounded">
                <span className="text-white/70 capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="text-emerald-400 font-bold">K={val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-white/30 text-xs mt-2">
        Example: A Champion at National level = 200 x 6.0 = 1,200 pts. With Highest Scorer award = 1,200 + (40 x 6.0) = 1,440 pts total.
      </p>
    </div>
  );
}
